/* Cltchworks — global.js
 * Vanilla only. Every behaviour here is an enhancement: the site renders and
 * works with JavaScript disabled.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
   * Scroll reveal — .rv elements fade up as they enter the viewport.
   * Without JS (or with reduced motion) they are simply visible.
   * ------------------------------------------------------------------- */
  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.rv:not(.in)');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.1 }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
   * <details> menus: close on Escape, and close the currency picker when a
   * click lands outside it. The mobile drawer stays open on outside clicks —
   * it covers the viewport, so there is no outside to click.
   * ------------------------------------------------------------------- */
  function closeDetails(el) {
    if (el && el.open) el.removeAttribute('open');
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details.drawer[open], details.loc[open]').forEach(function (el) {
      closeDetails(el);
      var summary = el.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  document.addEventListener('click', function (event) {
    document.querySelectorAll('details.loc[open]').forEach(function (el) {
      if (!el.contains(event.target)) closeDetails(el);
    });
  });

  /* Lock background scroll while the mobile drawer is open. */
  document.addEventListener('toggle', function (event) {
    var el = event.target;
    if (!el.classList || !el.classList.contains('drawer')) return;
    document.body.style.overflow = el.open ? 'hidden' : '';
  }, true);

  /* ---------------------------------------------------------------------
   * Judge.me medal captions — snippets/judgeme-badges.liquid.
   *
   * A medal nobody can read is decoration. Judge.me already writes the
   * criterion on each medal as a `title` ("Diamond Authentic Shop. 100% of
   * published reviews are verified reviews"), which is the right sentence
   * delivered the wrong way: a title tooltip wants a mouse and a second of
   * patience, and on a phone it does not exist at all.
   *
   * This lifts the text out and puts it on the page. Reading their attribute
   * rather than copying the wording into the theme is the point — the sentence
   * carries the tier and the threshold, and both move when the shop's record
   * moves. Nothing here invents or paraphrases a claim about a medal.
   *
   * Judge.me ships the medals hidden and unhides them from its own script, so
   * the captions wait for that: describing medals nobody can see would be
   * worse than saying nothing.
   * ------------------------------------------------------------------- */
  function initMedalNotes(root) {
    (root || document).querySelectorAll('.jmb-medals').forEach(function (medals) {
      var wrapper = medals.querySelector('.jdgm-medals-wrapper');
      var block = medals.closest('.jmb-medalblock');
      var footnote = block && block.querySelector('[data-medal-notes]');

      var write = function () {
        /* Their own class is the signal that the medals are on screen. */
        if (wrapper && wrapper.classList.contains('jdgm-hidden')) return false;

        var found = medals.querySelectorAll('.jdgm-medal-wrapper');
        if (!found.length) return false;

        var written = 0;
        found.forEach(function (medal) {
          if (medal.querySelector('.jmb-note')) {
            written++;
            return;
          }

          var text = (medal.getAttribute('title') || '').trim();
          if (!text) return;

          /* "<Name>. <Criterion>" — the first full stop is the seam. A medal
             whose title is one clause still gets a caption, just no detail. */
          var seam = text.indexOf('. ');
          var name = seam > -1 ? text.slice(0, seam) : text;
          var detail = seam > -1 ? text.slice(seam + 2).replace(/\.$/, '') : '';

          var note = document.createElement('div');
          note.className = 'jmb-note';

          var label = document.createElement('b');
          label.textContent = name;
          note.appendChild(label);

          if (detail) {
            var body = document.createElement('span');
            body.textContent = detail.charAt(0).toUpperCase() + detail.slice(1) + '.';
            note.appendChild(body);
          }

          /* Beside the medal, inside their own wrapper, so the sentence and
             the artwork it explains cannot be read apart. */
          medal.appendChild(note);

          /* The tooltip said exactly this and said it to nobody on a phone. */
          medal.removeAttribute('title');
          written++;
        });

        if (!written) return false;
        if (footnote) footnote.hidden = false;
        return true;
      };

      if (write()) return;
      if (!wrapper || !('MutationObserver' in window)) return;

      /* Their script unhides the wrapper when it has finished; catch that one
         class change and stop watching. */
      var observer = new MutationObserver(function () {
        if (write()) observer.disconnect();
      });
      observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });

      /* And stop watching regardless, so a shop that never qualifies is not
         left with an observer running for the life of the page. */
      window.setTimeout(function () {
        observer.disconnect();
      }, 10000);
    });
  }

  /* ---------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------- */
  function init() {
    initReveal(document);
    initQuickAdd(document);
    initDealerPins(document);
    initBoardWires(document);
    initMedalNotes(document);
    initCart(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-scan when a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
    initQuickAdd(event.target);
    initDealerPins(event.target);
    initBoardWires(event.target);
    initMedalNotes(event.target);
    initCart(event.target);
  });

  window.CltchworksTheme = {
    initReveal: initReveal,
    initQuickAdd: initQuickAdd,
    initDealerPins: initDealerPins,
    initBoardWires: initBoardWires
  };

  /* ---------------------------------------------------------------------
   * Artiste board wires.
   *
   * Each ring on the board photograph is joined to its switch card by a
   * dotted line. Only the browser knows where a ring at 43% of a photograph
   * ends up once the photograph has been laid out, so the paths are measured
   * and drawn here rather than authored in the markup.
   *
   * Redrawn whenever the stage changes size, which covers the board image
   * arriving, a font loading and the window being resized. In the stacked
   * layout there is nothing to join across, so no path is drawn and each ring
   * shows its own label instead.
   *
   * Without JavaScript the rings still sit on the right switches and every
   * card still names its switch. The wire is the shortest route between the
   * two, not the only one.
   * ------------------------------------------------------------------- */
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function initBoardWires(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-board-stage]').forEach(function (stage) {
      var svg = stage.querySelector('[data-board-wires]');
      var marks = [].slice.call(stage.querySelectorAll('[data-board-mark]'));
      var slots = [].slice.call(stage.querySelectorAll('[data-board-slot]'));
      if (!svg || !marks.length) return;

      function draw() {
        var sr = stage.getBoundingClientRect();
        if (!sr.width) return;
        svg.setAttribute('viewBox', '0 0 ' + sr.width + ' ' + sr.height);
        while (svg.firstChild) svg.removeChild(svg.firstChild);

        // One grid column means the cards sit under the photograph, not
        // beside it, and a line between them would run through the caption.
        var cols = getComputedStyle(stage).gridTemplateColumns.split(' ').filter(Boolean);
        if (cols.length < 2) return;

        marks.forEach(function (mark) {
          var id = mark.getAttribute('data-board-mark');
          var card = stage.querySelector('[data-board-slot="' + id + '"]');
          if (!card) return;

          var mr = mark.getBoundingClientRect();
          var cr = card.getBoundingClientRect();
          var x1 = mr.right - sr.left;
          var y1 = mr.top + mr.height / 2 - sr.top;
          var x2 = cr.left - sr.left;
          var y2 = cr.top + cr.height / 2 - sr.top;
          if (x2 <= x1) return;

          var bow = (x2 - x1) * 0.55;
          var path = document.createElementNS(SVG_NS, 'path');
          path.setAttribute('d',
            'M' + x1 + ' ' + y1 +
            ' C' + (x1 + bow) + ' ' + y1 + ',' + (x2 - bow) + ' ' + y2 + ',' + x2 + ' ' + y2);
          path.setAttribute('data-wire', id);
          svg.appendChild(path);
        });
      }

      /* Ring, card and wire light together, from whichever end you are on. */
      function lit(id, on) {
        marks.forEach(function (m) {
          m.classList.toggle('is-lit', on && m.getAttribute('data-board-mark') === id);
        });
        slots.forEach(function (c) {
          c.classList.toggle('is-lit', on && c.getAttribute('data-board-slot') === id);
        });
        svg.querySelectorAll('path').forEach(function (w) {
          w.classList.toggle('is-lit', on && w.getAttribute('data-wire') === id);
        });
      }

      function pair(el, id) {
        ['mouseenter', 'focusin'].forEach(function (ev) {
          el.addEventListener(ev, function () { lit(id, true); });
        });
        ['mouseleave', 'focusout'].forEach(function (ev) {
          el.addEventListener(ev, function () { lit(id, false); });
        });
      }
      marks.forEach(function (m) { pair(m, m.getAttribute('data-board-mark')); });
      slots.forEach(function (c) { pair(c, c.getAttribute('data-board-slot')); });

      draw();
      if ('ResizeObserver' in window) new ResizeObserver(draw).observe(stage);
      window.addEventListener('resize', draw);
      var img = stage.querySelector('.ab-plate img');
      if (img && !img.complete) img.addEventListener('load', draw);
    });
  }


  /* ---------------------------------------------------------------------
   * Dealer map pins.
   *
   * One popup open at a time. The document listener closes whichever is open
   * when the click lands anywhere else — the pin's own handler stops the event
   * before it gets there, and the popup's does too so that clicking the link
   * inside one does not close it out from under the pointer.
   *
   * Without JS the pins render as buttons that do nothing, and the dealer list
   * underneath carries every shop, its address line and its link. Nothing on
   * this page is only reachable through the map.
   * ------------------------------------------------------------------- */
  function initDealerPins(root) {
    var scope = root || document;
    var pins = scope.querySelectorAll('[data-pin]');
    if (!pins.length) return;

    function closeAll() {
      document.querySelectorAll('[data-pin].is-open').forEach(function (pin) {
        pin.classList.remove('is-open');
        var button = pin.querySelector('.pin__btn');
        if (button) button.setAttribute('aria-expanded', 'false');
      });
    }

    pins.forEach(function (pin) {
      var button = pin.querySelector('.pin__btn');
      var popup = pin.querySelector('.popup');
      if (!button) return;

      button.addEventListener('click', function (event) {
        event.stopPropagation();
        var wasOpen = pin.classList.contains('is-open');
        closeAll();
        if (!wasOpen) {
          pin.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
        }
      });

      if (popup) popup.addEventListener('click', function (event) { event.stopPropagation(); });
    });

    if (!document.documentElement.hasAttribute('data-pins-bound')) {
      document.documentElement.setAttribute('data-pins-bound', '');
      document.addEventListener('click', closeAll);
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeAll();
      });
    }
  }

  /* ---------------------------------------------------------------------
   * Quick add to cart, on the Store catalogue.
   *
   * The markup is a real product form posting to /cart/add, so with no JS the
   * browser posts it and lands on the cart — the add still works, it just
   * costs a page. Here we post it in the background instead and confirm on the
   * button, which is the whole point of a catalogue built for speed.
   * ------------------------------------------------------------------- */
  function initQuickAdd(root) {
    (root || document).querySelectorAll('form[data-quick-add]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var button = form.querySelector('button');
        var label = form.querySelector('[data-quick-add-label]');
        if (!button || !label || button.disabled) return;

        var original = label.textContent;
        button.disabled = true;

        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/javascript' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('cart');
            label.textContent = form.dataset.addedLabel || 'Added \u2713';
            button.classList.add('added');
            updateCartCount();
          })
          .catch(function () {
            /* Anything went wrong — hand it back to the browser, which will
               post the form properly and show whatever the error actually is
               rather than swallowing it. */
            form.removeAttribute('data-quick-add');
            form.submit();
          })
          .finally(function () {
            window.setTimeout(function () {
              label.textContent = original;
              button.classList.remove('added');
              button.disabled = false;
            }, 1600);
          });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Cart — live quantities.
   *
   * The cart used to be a plain form: type a number, press Update, wait for a
   * page load. Until you pressed Update nothing moved, so a shopper who
   * changed a quantity and looked at the price saw a cart that had ignored
   * them.
   *
   * Each change now goes to /cart/change.js and asks the same request to
   * re-render this section, so the line price, the subtotal, any discount and
   * the header count are all redrawn from one authoritative response. Doing
   * the arithmetic here instead would mean reimplementing line discounts and
   * currency formatting in JavaScript, and being wrong about them eventually.
   *
   * Everything still works with scripting off — the buttons are inert, the
   * input is a real updates[] field, and Update submits the form. That is why
   * .is-live is only added once this function has actually run.
   * ------------------------------------------------------------------- */
  function initCart(root) {
    var form = (root || document).querySelector('[data-cart-form]');
    if (!form || !window.fetch || !window.DOMParser) return;

    var sectionId = form.getAttribute('data-cart-section');
    var wrapper = document.getElementById('shopify-section-' + sectionId);
    if (!wrapper) return;

    form.classList.add('is-live');
    syncSteppers(form);

    var busy = false;

    function change(key, quantity, restore) {
      if (busy) return;
      busy = true;
      form.classList.add('is-busy');

      /* The note and the duties tick are not part of the cart until the form
         posts, so a re-render would throw away whatever the shopper had just
         typed. Carry them across by hand. */
      var note = form.querySelector('[data-cart-note]');
      var noteValue = note ? note.value : null;
      var duties = form.querySelector('#DutiesAck');
      var dutiesChecked = duties ? duties.checked : null;

      fetch(cartRoute('change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart');
          return response.json();
        })
        .then(function (cart) {
          var html = cart.sections && cart.sections[sectionId];
          if (!html) throw new Error('section');

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('shopify-section-' + sectionId);
          wrapper.innerHTML = fresh ? fresh.innerHTML : doc.body.innerHTML;

          var newNote = wrapper.querySelector('[data-cart-note]');
          if (newNote && noteValue !== null) newNote.value = noteValue;
          var newDuties = wrapper.querySelector('#DutiesAck');
          if (newDuties && dutiesChecked !== null) newDuties.checked = dutiesChecked;

          updateCartCount();
          initCart(wrapper);

          /* Put the keyboard back where it was. Without this every press of +
             throws focus to the top of the document, so a second press means
             finding the button again. */
          if (restore) {
            var again = wrapper.querySelector(
              '[data-cart-qty][data-key="' + cssEscape(restore.key) + '"]'
            );
            if (again) {
              var target = restore.step
                ? again.parentNode.querySelector('[data-cart-step="' + restore.step + '"]')
                : again;
              if (target && !target.disabled) target.focus();
            }
          }
        })
        .catch(function () {
          /* Hand it back to the browser: a normal form post applies the same
             change and shows a real error if something is genuinely wrong. */
          form.classList.remove('is-live');
          form.submit();
        })
        .finally(function () {
          busy = false;
          form.classList.remove('is-busy');
        });
    }

    form.addEventListener('click', function (event) {
      var button = event.target.closest('[data-cart-step]');
      if (!button) return;

      var input = button.parentNode.querySelector('[data-cart-qty]');
      if (!input) return;

      var step = parseInt(button.getAttribute('data-cart-step'), 10);
      var next = (parseInt(input.value, 10) || 0) + step;
      /* The minus button floors at 1; removing a line is what Remove is for. */
      if (next < 1) return;

      input.value = next;
      change(input.getAttribute('data-key'), next, {
        key: input.getAttribute('data-key'),
        step: button.getAttribute('data-cart-step')
      });
    });

    form.addEventListener('change', function (event) {
      var input = event.target.closest('[data-cart-qty]');
      if (input) {
        var quantity = parseInt(input.value, 10);
        if (isNaN(quantity) || quantity < 0) quantity = 0;
        input.value = quantity;
        change(input.getAttribute('data-key'), quantity, {
          key: input.getAttribute('data-key'),
          step: null
        });
        return;
      }

      /* The note used to be saved by the Update button, which is now hidden. */
      var note = event.target.closest('[data-cart-note]');
      if (note) {
        fetch(cartRoute('update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(function () {});
      }
    });
  }

  /* A minus button that would take the line to zero is dead, and says so. */
  function syncSteppers(form) {
    form.querySelectorAll('[data-cart-qty]').forEach(function (input) {
      var minus = input.parentNode.querySelector('[data-cart-step="-1"]');
      if (minus) minus.disabled = (parseInt(input.value, 10) || 0) <= 1;
    });
  }

  function cartRoute(endpoint) {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root + 'cart/' + endpoint;
  }

  /* Line keys contain a colon, which is a valid attribute value but not a
     valid bare selector. */
  function cssEscape(value) {
    return window.CSS && window.CSS.escape ? window.CSS.escape(value) : value;
  }

  /* The header's cart link is the only count on the page; without this it goes
     on saying (0) until the next navigation. */
  function updateCartCount() {
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

})();
